import { useCallback, useEffect, useState } from "react";
import { RECAPTCHA_CONFIG } from "@danishagro/shared/src/config/recaptcha.config";

declare global {
    interface Window {
        grecaptcha: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
            enterprise: {
                ready: (callback: () => void) => void;
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
            };
        };
    }
}

interface UseRecaptchaProps {
    siteKey: string;
    enabled?: boolean;
}

interface UseRecaptchaReturn {
    isReady: boolean;
    executeRecaptcha: (action: string) => Promise<string | null>;
    executeRecaptchaForCriticalAction: (action: string) => Promise<string | null>;
    loadRecaptcha: () => void;
    isCriticalAction: (action: string) => boolean;
}

export const useRecaptcha = ({
    siteKey,
    enabled = true,
}: UseRecaptchaProps): UseRecaptchaReturn => {
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const loadRecaptcha = useCallback(() => {
        if (!enabled || isLoading || isReady || typeof window === "undefined") return;

        // Validate site key before loading
        if (!siteKey || siteKey.trim() === "") {
            console.warn(
                "reCAPTCHA site key is not configured. Set REACT_APP_RECAPTCHA_SITE_KEY in your environment variables."
            );
            return;
        }

        // Check if reCAPTCHA is already loaded
        if (window.grecaptcha && window.grecaptcha.enterprise) {
            window.grecaptcha.enterprise.ready(() => {
                setIsReady(true);
            });
            return;
        }

        setIsLoading(true);

        // Create and append the reCAPTCHA Enterprise script
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            if (window.grecaptcha && window.grecaptcha.enterprise) {
                window.grecaptcha.enterprise.ready(() => {
                    setIsReady(true);
                    setIsLoading(false);
                });
            }
        };

        script.onerror = () => {
            console.error("Failed to load reCAPTCHA script");
            setIsLoading(false);
        };

        document.head.appendChild(script);
    }, [siteKey, enabled, isLoading, isReady]);

    const executeRecaptcha = useCallback(
        async (action: string): Promise<string | null> => {
            if (!enabled || !isReady || !window.grecaptcha || !window.grecaptcha.enterprise) {
                console.warn("reCAPTCHA Enterprise is not ready or not enabled");
                return null;
            }

            try {
                const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
                return token;
            } catch (error) {
                console.error("reCAPTCHA Enterprise execution failed:", error);
                return null;
            }
        },
        [siteKey, enabled, isReady]
    );

    // Execute reCAPTCHA for critical actions that require backend verification
    const executeRecaptchaForCriticalAction = useCallback(
        async (action: string): Promise<string | null> => {
            const token = await executeRecaptcha(action);
            if (!token) {
                throw new Error(`reCAPTCHA verification failed for critical action: ${action}`);
            }
            return token;
        },
        [executeRecaptcha]
    );

    // Check if an action is critical and requires backend verification
    const isCriticalAction = useCallback((action: string): boolean => {
        return RECAPTCHA_CONFIG.CRITICAL_ACTIONS.includes(
            action as (typeof RECAPTCHA_CONFIG.CRITICAL_ACTIONS)[number]
        );
    }, []);

    useEffect(() => {
        if (enabled) {
            loadRecaptcha();
        }
    }, [loadRecaptcha, enabled]);

    return {
        isReady,
        executeRecaptcha,
        executeRecaptchaForCriticalAction,
        loadRecaptcha,
        isCriticalAction,
    };
};
